<template>
  <section v-if="restaurants.length" id="dining" class="recurring-section">
    <v-container>
      <p>Food and Tradition</p>
      <h2>Dining Along the Way</h2>
      <div class="card-grid">
        <article
          v-for="restaurant in restaurants"
          :key="restaurant.name"
          :id="restaurantAnchorId(restaurant)"
          class="recurring-card"
          :class="{ 'recurring-card-clickable': detailRoute(restaurant) }"
          :role="detailRoute(restaurant) ? 'link' : null"
          :tabindex="detailRoute(restaurant) ? 0 : null"
          @click="openDetails(restaurant)"
          @keydown.enter="openDetails(restaurant)"
        >
          <v-img
            v-if="restaurant.image"
            :src="restaurant.image | mediaSrc"
            :alt="restaurant.imageAlt || restaurant.name"
            :position="restaurant.imagePosition || 'center center'"
            aspect-ratio="1.35"
            class="restaurant-image"
          />
          <div class="card-meta">
            <span>{{ restaurant.location }}</span>
            <strong v-if="restaurant.visitCount > 1">Reckon Tradition</strong>
          </div>
          <h3>{{ restaurant.name }}</h3>
          <p v-if="restaurant.description" class="restaurant-description">{{ restaurant.description }}</p>
          <p class="visit-count">Visited {{ restaurant.visitCount }} {{ restaurant.visitCount === 1 ? 'time' : 'times' }}</p>
          <div class="year-list" :aria-label="`Years ${restaurant.name} was visited`">
            <span v-for="year in restaurant.years" :key="year">{{ year }}</span>
          </div>
          <a
            v-if="restaurant.websiteUrl"
            :href="restaurant.websiteUrl"
            class="website-link"
            target="_blank"
            rel="noopener noreferrer"
            @click.stop
          >
            Visit website
            <v-icon small>mdi-open-in-new</v-icon>
          </a>
        </article>
      </div>
    </v-container>
  </section>
</template>

<script>
import {
  eventEntityRoute,
  normalizeEntityKey,
  resolveEventEntityDetails,
} from '../../../lib/reckon-way/entityDetails';

export default {
  name: 'RecurringRestaurantGrid',
  props: {
    restaurants: { type: Array, default: () => [] },
    event: { type: Object, required: true },
  },
  methods: {
    restaurantAnchorId(restaurant) {
      return `restaurant-${normalizeEntityKey(restaurant.name)}`;
    },
    detailRoute(restaurant) {
      const details = resolveEventEntityDetails({ event: this.event, sectionKey: 'dining', item: restaurant });
      return details?.media.length ? eventEntityRoute(this.event, 'dining', restaurant) : null;
    },
    openDetails(restaurant) {
      const route = this.detailRoute(restaurant);
      if (route) this.$router.push(route);
    },
  },
};
</script>

<style scoped>
.recurring-section { background: #080808; border-top: 1px solid #242424; padding: 76px 0; scroll-margin-top: 68px; }
.recurring-section > .container > p { color: #e23232; font-family: "Oswald", sans-serif; font-weight: 800; text-transform: uppercase; }
h2 { color: #fff; font-family: "Oswald", sans-serif; font-size: clamp(2.2rem, 5vw, 3.2rem); margin: 0 0 28px; }
.card-grid { display: grid; gap: 16px; grid-template-columns: repeat(3, minmax(0, 1fr)); }
.recurring-card { background: linear-gradient(145deg, #171717, #0b0b0b); border: 1px solid #303030; border-radius: 12px; padding: 23px; }
.recurring-card:target { border-color: #f0b13c; box-shadow: 0 0 0 3px rgba(240, 177, 60, .16); }
.recurring-card-clickable { cursor: pointer; transition: border-color 160ms ease, transform 160ms ease; }
.recurring-card-clickable:hover, .recurring-card-clickable:focus { border-color: #e23232; outline: none; transform: translateY(-2px); }
.restaurant-image { border-bottom: 1px solid #303030; border-radius: 11px 11px 0 0; margin: -23px -23px 20px; }
.card-meta { align-items: center; color: #999; display: flex; font-size: 0.72rem; font-weight: 800; gap: 10px; justify-content: space-between; text-transform: uppercase; }
.card-meta strong { background: rgba(240, 177, 60, 0.13); border: 1px solid rgba(240, 177, 60, 0.45); border-radius: 999px; color: #f0b13c; padding: 5px 8px; }
h3 { color: #fff; font-family: "Oswald", sans-serif; font-size: 1.55rem; margin: 14px 0 8px; }
.recurring-card p { color: #aaa; }
.restaurant-description { line-height: 1.6; margin: 0 0 14px; }
.visit-count { margin: 0 0 18px; }
.year-list { display: flex; flex-wrap: wrap; gap: 7px; }
.year-list span { background: #090909; border: 1px solid #343434; border-radius: 999px; color: #ddd; padding: 5px 10px; }
.website-link { align-items: center; color: #f0b13c; display: inline-flex; font-size: .78rem; font-weight: 800; gap: 5px; margin-top: 20px; text-decoration: none; text-transform: uppercase; }
.website-link:hover, .website-link:focus-visible { color: #fff; text-decoration: underline; }
.website-link .v-icon { color: currentColor; }
@media (max-width: 900px) { .card-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 600px) { .card-grid { grid-template-columns: 1fr; } }
</style>
